/// <reference types="@webgpu/types" />
//neuralNetwork.ts — GPU resources for one source resolution: pipelines, ping-pong feature
//buffers and the per-frame command encoding. The whole graph is one compute pass (four
//dispatches, WebGPU inserts the barriers between them) plus one fullscreen draw, submitted
//as a single command buffer.

import {
	displayShader,
	firstConvShader,
	hiddenConvShader,
	WORKGROUP_SIZE,
	type SourceSize,
} from './networkShaders'
import { loadNetworkWeights } from './networkWeights'

export const OUTPUT_SCALE = 2

//One vec4<f32> of features per source pixel, per buffer.
const FEATURE_BYTES_PER_PIXEL = 16
//Only the previous layer's output is ever read, so two buffers ping-pong the whole chain.
const FEATURE_BUFFERS = 2

export interface NeuralNetworkGraph {
	encode(encoder: GPUCommandEncoder, source: GPUExternalTexture, target: GPUTextureView): void
	destroy(): void
}

export async function createNetworkGraph(
	device: GPUDevice,
	size: SourceSize,
	format: GPUTextureFormat,
): Promise<NeuralNetworkGraph> {
	const featureBytes = size.width * size.height * FEATURE_BYTES_PER_PIXEL
	if (featureBytes > device.limits.maxStorageBufferBindingSize) {
		throw new Error(
			`Source ${size.width}×${size.height} needs ${featureBytes} B feature buffers, ` +
				`over this device's ${device.limits.maxStorageBufferBindingSize} B storage limit`,
		)
	}

	const weights = loadNetworkWeights()
	const uniforms = [weights.first, ...weights.hidden].map((data, index) =>
		createUniformBuffer(device, data, `conv-${index}-params`),
	)
	const features = Array.from({ length: FEATURE_BUFFERS }, (_, index) =>
		device.createBuffer({
			label: `conv-features-${index}`,
			size: featureBytes,
			usage: GPUBufferUsage.STORAGE,
		}),
	)

	const [firstPipeline, hiddenPipeline, displayPipeline] = await Promise.all([
		device.createComputePipelineAsync({
			label: 'conv-first',
			layout: 'auto',
			compute: { module: device.createShaderModule({ code: firstConvShader(size) }), entryPoint: 'main' },
		}),
		device.createComputePipelineAsync({
			label: 'conv-hidden',
			layout: 'auto',
			compute: { module: device.createShaderModule({ code: hiddenConvShader(size) }), entryPoint: 'main' },
		}),
		createDisplayPipeline(device, size, format),
	])

	const firstLayout = firstPipeline.getBindGroupLayout(0)
	const displayLayout = displayPipeline.getBindGroupLayout(0)
	const hiddenLayout = hiddenPipeline.getBindGroupLayout(0)

	//first conv writes features[0], then each hidden layer flips between the two buffers.
	const hiddenGroups = weights.hidden.map((_, index) =>
		device.createBindGroup({
			label: `conv-hidden-${index}`,
			layout: hiddenLayout,
			entries: [
				{ binding: 0, resource: { buffer: features[index % FEATURE_BUFFERS] } },
				{ binding: 1, resource: { buffer: uniforms[index + 1] } },
				{ binding: 2, resource: { buffer: features[(index + 1) % FEATURE_BUFFERS] } },
			],
		}),
	)
	const residual = features[weights.hidden.length % FEATURE_BUFFERS]
	const sampler = device.createSampler({ magFilter: 'linear', minFilter: 'linear' })

	const groupsX = Math.ceil(size.width / WORKGROUP_SIZE)
	const groupsY = Math.ceil(size.height / WORKGROUP_SIZE)

	return {
		encode(encoder, source, target) {
			//External textures expire at the end of the task, so their bind groups are per frame.
			const compute = encoder.beginComputePass({ label: 'anime4k-cnn-2x-s' })
			compute.setPipeline(firstPipeline)
			compute.setBindGroup(
				0,
				device.createBindGroup({
					label: 'conv-first',
					layout: firstLayout,
					entries: [
						{ binding: 0, resource: source },
						{ binding: 1, resource: { buffer: uniforms[0] } },
						{ binding: 2, resource: { buffer: features[0] } },
					],
				}),
			)
			compute.dispatchWorkgroups(groupsX, groupsY)
			compute.setPipeline(hiddenPipeline)
			for (const group of hiddenGroups) {
				compute.setBindGroup(0, group)
				compute.dispatchWorkgroups(groupsX, groupsY)
			}
			compute.end()

			const display = encoder.beginRenderPass({
				label: 'display',
				colorAttachments: [
					{ view: target, loadOp: 'clear', storeOp: 'store', clearValue: { r: 0, g: 0, b: 0, a: 1 } },
				],
			})
			display.setPipeline(displayPipeline)
			display.setBindGroup(
				0,
				device.createBindGroup({
					label: 'display',
					layout: displayLayout,
					entries: [
						{ binding: 0, resource: { buffer: residual } },
						{ binding: 1, resource: source },
						{ binding: 2, resource: sampler },
					],
				}),
			)
			display.draw(3)
			display.end()
		},

		destroy() {
			for (const buffer of [...uniforms, ...features]) buffer.destroy()
		},
	}
}

function createDisplayPipeline(
	device: GPUDevice,
	size: SourceSize,
	format: GPUTextureFormat,
): Promise<GPURenderPipeline> {
	const module = device.createShaderModule({ code: displayShader(size) })
	return device.createRenderPipelineAsync({
		label: 'display',
		layout: 'auto',
		vertex: { module, entryPoint: 'vertexMain' },
		fragment: { module, entryPoint: 'fragmentMain', targets: [{ format }] },
		primitive: { topology: 'triangle-list' },
	})
}

function createUniformBuffer(device: GPUDevice, data: Float32Array, label: string): GPUBuffer {
	const buffer = device.createBuffer({
		label,
		size: data.byteLength,
		usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
	})
	//Written as a raw byte range: the typed-array overload does not accept Float32Array here.
	device.queue.writeBuffer(buffer, 0, data.buffer, data.byteOffset, data.byteLength)
	return buffer
}
